import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { WithCategoryLeadersContextHOC } from "app/pages/industry-analysis/category-leaders/components/CategoryLeadersContext/WithCategoryLeadersContextHOC";
import { TrafficAcquisitionLeaders } from "app/pages/industry-analysis/category-leaders/pages/TrafficAcquisitionLeaders";
import { IndustryAnalysisLoyaltyContainer } from "app/pages/industry-analysis/category-loyalty/IndustryAnalysisLoyaltyContainer";
import { CategoryShare } from "app/pages/industry-analysis/category-share/CategoryShare";
import { resolveOverrideDatePickerPreset } from "app/pages/industry-analysis/config/resolveOverrideDatePickerPreset";
import { IndustryAnalysisDemographicsContainer } from "app/pages/industry-analysis/demographics/IndustryAnalysisDemographicsContainer";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";
import { IndustryAnalysisGeographyContainer } from "app/pages/industry-analysis/geography/IndustryAnalysisGeographyContainer";
import { IndustryAnalysisRoute } from "app/pages/industry-analysis/routes/IndustryAnalysisRoute";
import { WebsiteRankingsAllPage } from "app/pages/industry-analysis/website-rankings/WebsiteRankingsAllPage";
import { WebsiteRankingsStartPage } from "app/pages/industry-analysis/website-rankings/WebsiteRankingsStartPage";
import { DemandAnalysisHomePage } from "app/pages/market-research/DemandAnalysisHomePage";
import { DemandAnalysisRoot } from "app/pages/market-research/DemandAnalysisRoot";
import { DemandAnalysisSearchTrends } from "app/pages/market-research/DemandAnalysisSearchTrends";
import { DemandAnalysisSearchTrendsRoot } from "app/pages/market-research/DemandAnalysisSearchTrendsRoot";
import {
    redirectToSearchTrendsComparison,
    redirectFromLegacyEntityParams,
    redirectFromLegacyEntityTypes,
    demandAnalysisSearchTrendsPageViewTracking,
    ensureDemandAnalysisCountryParam,
} from "app/pages/market-research/features/router";
// fallow-ignore-next-line boundary-violation -- single-spa route config mounts market-research page component
import { TopicsHeatmapPage } from "app/pages/market-research/features/topics-heatmap/TopicsHeatmapPage";
import { DEMAND_ANALYSIS_HOME_STATE_NAME, TOPICS_HEATMAP_STATE_NAME } from "app/pages/market-research/shared/constants";
import { SneakpeekQuery } from "app/pages/sneakpeek/SneakpeekQuery";
import { SneakpeekResultsPage } from "app/pages/sneakpeek/SneakpeekResultsPage";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { onRedirectIndustryAnalysis } from "app/routes/handlers/filter-consistency/pipes/industry-analysis/onRedirectIndustryAnalysis";
import { onRedirectIndustryAnalysisLeaders } from "app/routes/handlers/filter-consistency/pipes/industry-analysis/onRedirectIndustryAnalysisLeaders";
import { onRedirectKeywordAnalysis } from "app/routes/handlers/filter-consistency/pipes/keyword-analysis/onRedirectKeywordAnalysis";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { AssetsService } from "app/services/AssetsService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { BasicDurations } from "app/services/DurationService";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { AUTOCOMPLETE_DEMAND_ANALYSIS_TEXT_CONTENT } from "pro-features/components/AutocompleteDemandAnalysisUnified/utils/consts";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { appMarketAnalysisConfig } from "single-spa/react-app/config/solutions25/industryResearchAppMarketAnalysisConfig";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

export const SELECTED_TAB_QUERY_PARAM = "selectedTab";

const webmarketAnalysisConfig: RouterStateConfig = {
    digitalsuite_marketresearch_webmarketanalysis: {
        abstract: true,
        parent: "digitalsuite_marketresearch",
        url: "/markets/webmarketanalysis",
        configId: "IndustryAnalysis",
        component: IndustryAnalysisRoute,
        data: {
            menuKbItems: false,
            disableRecording: true,
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
        pageId: {
            section: "industryAnalysis",
        },
        trackingId: {
            section: "Competitive Research",
        },
        homeState: "digitalsuite_webmarketanalysis_home",
        navBarContainerClassName: "accordion-side-bar",
        entityType: "industry",
    },
    digitalsuite_webmarketanalysis_home: {
        parent: "digitalsuite_marketresearch",
        url: "/markets/webmarketanalysis/home",
        component: WithSegmentsLoading((props: FixmeType) => {
            return (
                <IndustryAnalysisRoute shouldLogErrorFallback={false}>
                    <WebsiteRankingsStartPage {...props} />
                </IndustryAnalysisRoute>
            );
        }),
        configId: "WebMarketAnalysisOverviewHome",
        configIdDeps: ["IndustryAnalysis", "IndustryAnalysisOverview", "MobileWeb", "IATopWebsitesExtended"],
        pageId: {
            section: "marketresearch",
            subSection: "marketresearch",
            subSubSection: "analyzewebsites",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Home",
            subSubSection: "Home",
        },
        pageTitle: "industryanalysis.websiteRankings.title",
        entityType: "industry",
        overrideParams: () => ({
            duration: BasicDurations.LAST_MONTH,
            country: 999,
            webSource: devicesTypes.TOTAL,
        }),
        data: {
            filtersConfig: {
                duration: FiltersEnum.DISABLED,
                country: FiltersEnum.DISABLED,
                webSource: FiltersEnum.DISABLED,
            },
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_overview: {
        redirectTo: "digitalsuite_marketresearch",
    },
    digitalsuite_marketresearch_webmarketanalysis_mapping: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        url: "/mapping/:category/:country/:duration?webSource",
        component: WebsiteRankingsAllPage,
        stateNameForEductionBar: "marketresearch_webmarketanalysis_mapping",
        configId: "IATopWebsitesExtended",
        overrideDatepickerPreset: ["1m", "3m"],
        configIdDeps: ["MobileWeb", "IndustryAnalysis", "IATopWebsitesExtended"],
        redirectTo: redirectPipe(onRedirectIndustryAnalysisLeaders()),
        pageId: {
            section: "industryAnalysis",
            subSection: "topSites",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Overview",
            subSubSection: "topWebsites",
        },
        clearSearch: true,
        pageTitle: "industryanalysis.websiteRankings.title",
        pageSubtitle: "industryAnalysis.topSites.subtitle",
        defaultQueryParams: {
            webSource: "Total",
        },
        homeState: "digitalsuite_webmarketanalysis_home",
        hideCalendar: true,
        params: {
            category: { type: "string", raw: true },
            ...filterConsistencyParams,
        },
        isSecondaryBarOpen: true,
        customData: {
            filters: {
                duration: false, // 'false' for visible and disabled filter
                country: true,
                category: true,
                unsupportedCountryTooltip: "topsites.unsupportedCountry.tooltip",
            },
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_trends: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        url: "/trends/:category/:country/:duration?webSource?mtd",
        component: CategoryShare,
        stateNameForEductionBar: "marketresearch_webmarketanalysis_trends",
        configId: "CategoryShare",
        configIdDeps: ["MobileWeb", "IndustryAnalysis"],
        // @ts-ignore FIXME: strict migration - Type 'string[] | null' is not assignable to type 'string[] | undefined' — helper returns null preserved for behavior; IRouterState type expects undefined
        get overrideDatepickerPreset() {
            return resolveOverrideDatePickerPreset();
        },
        pageId: {
            section: "industryAnalysis",
            subSection: "categoryShare",
        },
        redirectTo: redirectPipe(onRedirectIndustryAnalysis()),
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Overview",
            subSubSection: "categoryShare",
        },
        icon: "sw-icon-widget-trafficshare",
        customData: {
            notPermittedConfig: {
                image: AssetsService.assetUrl("images/IACategoryShare.png"),
                description: "ia.categoryshare.desc",
            },
            filters: {
                duration: true,
                country: true,
                category: true,
                webSource: true,
            },
        },
        defaultQueryParams: {
            webSource: "Total",
            mtd: false,
        },
        clearSearch: true,
        pageTitle: "sidebar.market.analysis.web.overview.trends.title.market",
        homeState: "digitalsuite_webmarketanalysis_home",
        params: {
            category: { type: "string", raw: true },
            ...filterConsistencyParams,
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_demographics: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        configId: "IndustryAnalysisDemographics",
        configIdDeps: ["MobileWeb"],
        url: "/demographics/:category/:country/:duration?webSource",
        component: IndustryAnalysisDemographicsContainer,
        stateNameForEductionBar: "marketresearch_webmarketanalysis_demographics",
        redirectTo: redirectPipe(onRedirectIndustryAnalysis()),
        data: {
            pageViewTracking: null,
        },
        pageId: {
            section: "industryAnalysis",
            subSection: "overview",
            subSubSection: "demographics",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Audience",
            subSubSection: "demographics",
        },
        defaultQueryParams: {
            webSource: "Total",
            country: "999",
        },
        pageTitle: "industryAnalysis.demographics.page.title",
        homeState: "digitalsuite_webmarketanalysis_home",
        params: {
            category: { type: "string", raw: true },
            country: "999",
            ...filterConsistencyParams,
        },
        customData: {
            filters: {
                duration: true,
                country: true,
                category: true,
                webSource: true,
            },
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_geography: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        url: "/geography/:category/:country/:duration?webSource",
        stateNameForEductionBar: "marketresearch_webmarketanalysis_geography",
        component: IndustryAnalysisGeographyContainer,
        configId: "IndustryAnalysisGeography",
        redirectTo: redirectPipe(onRedirectIndustryAnalysis()),
        pageId: {
            section: "industryAnalysis",
            subSection: "geography",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Audience",
            subSubSection: "geography",
        },
        clearSearch: true,
        pageTitle: "digitalsuite.industryAnalysis.geography.page.title",
        pageSubtitle: "industryAnalysis.geography.page.subtitle",
        data: {
            filtersConfig: {
                country: FiltersEnum.HIDDEN,
            },
        },
        homeState: "digitalsuite_webmarketanalysis_home",
        params: {
            category: { type: "string", raw: true },
            ...filterConsistencyParams,
        },
        skipCountryCheck: true,
        customData: {
            filters: {
                duration: true,
                country: false,
                category: true,
            },
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_loyalty: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        url: "/audience-loyalty/:category/:country/:duration?webSource",
        component: IndustryAnalysisLoyaltyContainer,
        stateNameForEductionBar: "marketresearch_webmarketanalysis_loyalty",
        configId: "IndustryAnalysisAudienceLoyalty",
        redirectTo: redirectPipe(onRedirectIndustryAnalysis()),
        pageId: {
            section: "industryAnalysis",
            subSection: "audience",
            subSubSection: "loyalty",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Audience",
            subSubSection: "loyalty",
        },
        icon: "sw-icon-audience",
        pageTitle: "industryAnalysis.loyalty.title",
        pageSubtitle: "industryAnalysis.loyalty.subtitle",
        isVirtualSupported: false,
        isUSStatesSupported: false,
        overrideDatepickerPreset: ["3m", "6m", "12m", "25m", "37m"],
        minDurationRange: 3,
        // hideCalendar: true,
        defaultQueryParams: {
            webSource: "Desktop",
            country: 999,
        },
        data: {
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        homeState: "digitalsuite_webmarketanalysis_home",
        params: {
            category: { type: "string", raw: true },
            country: "999",
            ...filterConsistencyParams,
        },
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings: {
        parent: "digitalsuite_marketresearch_webmarketanalysis",
        url: `/rankings/:category/:country/:duration?webSource?${SELECTED_TAB_QUERY_PARAM}`,
        component: WithCategoryLeadersContextHOC(TrafficAcquisitionLeaders),
        stateNameForEductionBar: "marketresearch_webmarketanalysis_rankings_search",
        configId: "CategoryLeaders",
        pageId: {
            section: "industryAnalysis",
            subSection: "categoryLeaders",
        },
        defaultQueryParams: {
            webSource: "Total",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Web Industry Analysis/Traffic Acquisition Leaders",
            subSubSection: "CategoryLeadersAll",
        },
        icon: "sw-icon-leaders",
        clearSearch: true,
        pageSubtitle: "industryAnalysis.categoryLeaders.subtitle",
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                duration: FiltersEnum.DISABLED,
                webSource: FiltersEnum.DISABLED,
            },
        },
        pageTitle: "industryanalysis.websiteRankings.title",
        homeState: "digitalsuite_webmarketanalysis_home",
        redirectTo: redirectPipe(onRedirectIndustryAnalysis()),
        params: {
            category: { type: "string", raw: true },
            [SELECTED_TAB_QUERY_PARAM]: { type: "string", dynamic: false },
            ...filterConsistencyParams,
        },
    },
};

export const industryResearchConfig: RouterStateConfig = {
    ...webmarketAnalysisConfig,
    ...appMarketAnalysisConfig,
    digitalsuite_marketresearch_keywordmarketanalysis_home: {
        parent: "digitalsuite_marketresearch",
        url: "/marketresearch/keywordmarketanalysis/home",
        component: () => {
            const targetComponent = isAvailable(swSettings.components.DemandSearchTrends)
                ? KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE
                : "digitalsuite_marketresearch_keywordmarketanalysis_total";

            return (
                <ScrollContainer>
                    <DemandAnalysisHomePage
                        pageState={targetComponent}
                        title="demand.analysis.home.title"
                        ctaButtonText="demand.analysis.home.add_keyword_list"
                        saveKeywordGroupProps={{
                            targetStateOnSave: targetComponent,
                            showToast: false,
                        }}
                    />
                </ScrollContainer>
            );
        },
        redirectTo: redirectPipe(onRedirectKeywordAnalysis()),
        params: { ...filterConsistencyParams },
        configId: "TopicMarketAnalysisHome",
        secondaryBarType: "DigitalSuiteMarketResearch",
        isSecondaryBarOpen: true,
        configIdDeps: ["KeywordAnalysisTotal", "IndustryAnalysisSearchTrends"],
        skipCountryCheck: true,
        pageId: {
            section: "marketresearch",
            subSection: "marketresearch",
            subSubSection: "analyzekeywords",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Search Interest Analysis/Home",
            // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined' — explicit null retained for tracking behavior
            subSubSection: null,
        },
        pageTitle: "marketintelligence.marketresearch.webmarketanalysis.homepage.title",
    },
    [DEMAND_ANALYSIS_HOME_STATE_NAME]: {
        parent: "digitalsuite_marketresearch",
        url: "/marketresearch/keywordmarketresearch/home",
        component: () => {
            const targetComponent = isAvailable(swSettings.components.DemandSearchTrends)
                ? KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE
                : "digitalsuite_marketresearch_keywordmarketanalysis_total";

            return (
                <ScrollContainer>
                    <DemandAnalysisHomePage
                        supportCountryFilters
                        useAutogeneratedKeywordGroupVersion2
                        textContent={AUTOCOMPLETE_DEMAND_ANALYSIS_TEXT_CONTENT}
                        pageState={targetComponent}
                        title="demand.analysis.home.title"
                        subtitle="demand.analysis.home.subtitle"
                        ctaButtonText="demand.analysis.home.add_keyword_list"
                        saveKeywordGroupProps={{
                            targetStateOnSave: targetComponent,
                            showToast: false,
                        }}
                        mode="full"
                    />
                </ScrollContainer>
            );
        },
        redirectTo: redirectPipe(ensureDemandAnalysisCountryParam, onRedirectKeywordAnalysis()),
        onEnter: () => {
            void keywordGroupsQueryClient.prefetchUserGroups();
            void keywordGroupsQueryClient.prefetchSharedGroups();
        },
        params: {
            ...filterConsistencyParams,
            country: { dynamic: true },
        },
        configId: "TopicMarketAnalysisHome",
        secondaryBarType: "DigitalSuiteMarketResearch",
        isSecondaryBarOpen: true,
        configIdDeps: ["KeywordAnalysisTotal", "IndustryAnalysisSearchTrends"],
        skipCountryCheck: true,
        pageId: {
            section: "marketresearch",
            subSection: "marketresearch",
            subSubSection: "analyzekeywords",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Search Interest Analysis/Home",
            // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string | undefined' — explicit null retained for tracking behavior
            subSubSection: null,
        },
        pageTitle: "marketintelligence.marketresearch.webmarketanalysis.homepage.title",
        entityType: "demandKeywords",
    },
    [TOPICS_HEATMAP_STATE_NAME]: {
        parent: "digitalsuite_marketresearch",
        url: "/marketresearch/keywordmarketresearch/topics/heatmap?country&webSource",
        component: TopicsHeatmapPage,
        redirectTo: ensureDemandAnalysisCountryParam,
        params: {
            ...filterConsistencyParams,
            country: { dynamic: true },
            duration: "28d",
        },
        configId: "DemandSearchTrends",
        secondaryBarType: "DigitalSuiteMarketResearch",
        isSecondaryBarOpen: true,
        skipCountryCheck: true,
        pageId: {
            section: "marketresearch",
            subSection: "marketresearch",
            subSubSection: "topicsHeatmap",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Search Interest Analysis",
            subSubSection: "TopicsHeatmap",
        },
        pageTitle: "demand_analysis.topics_heatmap.page.title",
        data: {
            filtersConfig: {
                duration: FiltersEnum.DISABLED,
                webSource: FiltersEnum.DISABLED,
            },
        },
    },
    digitalsuite_marketresearch_keywordmarketanalysis: {
        abstract: true,
        parent: "digitalsuite_marketresearch",
        url: "/marketresearch/keywordmarketanalysis",
        component: DemandAnalysisRoot,
        configId: "KeywordAnalysis",
        secondaryBarType: "DigitalSuiteMarketResearch",
        isSecondaryBarOpen: true,
        data: {
            menuKbItems: false,
            disableRecording: true,
        },
        pageId: {
            section: "keywordAnalysis",
        },
        trackingId: {
            section: "Competitive Research",
        },
    },

    /*
     * This is a legacy route that is used for the previous version of the Search Trends page.
     * We only need to keep it for a while, until the users stop opening it (from bookmarks, emails, etc).
     */
    DIGITALSUITE_MARKETRESEARCH_KEYWORDMARKETANALYSIS_SEARCH_LEGACY_WILL_BE_REMOVED: {
        parent: "digitalsuite_marketresearch_keywordmarketanalysis",
        url: "/search/:country/:duration/:keyword/:subpage?webSource&mtd&isTerm&comparedDuration",
        redirectTo: redirectToSearchTrendsComparison,
    },
    digitalsuite_marketresearch_keywordmarketanalysis_search: {
        abstract: true,
        parent: "digitalsuite_marketresearch_keywordmarketanalysis",
        url: "search?country&webSource&duration&comparedDuration&autocomplete&id",
        component: DemandAnalysisSearchTrendsRoot,
        configId: "KeywordAnalysis",
        onEnter: () => {
            void keywordGroupsQueryClient.prefetchUserGroups();
            void keywordGroupsQueryClient.prefetchSharedGroups();
        },
        params: {
            duration: "12m",
            autocomplete: { dynamic: true },
            id: { array: true, dynamic: true },
            searchVolumeMTD: { value: "off", dynamic: true },
            keywordsTrendsMTD: { value: "off", dynamic: true },
        },
        pageId: {
            section: "keywordAnalysis",
        },
        trackingId: {
            section: "Competitive Research",
        },
        pageTitle: "keywordAnalysis.page.title",
        entityType: "demandKeywords",
    },
    [KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE]: {
        parent: "digitalsuite_marketresearch_keywordmarketanalysis_search",
        url: "/demand-search-trends",
        component: DemandAnalysisSearchTrends,
        secondaryBarType3: SecondarySideBarType3.None,
        stateNameForEductionBar: "marketresearch_keywordmarketanalysis_demand_search_trends",
        redirectTo: redirectPipe(
            redirectFromLegacyEntityTypes,
            redirectFromLegacyEntityParams,
            onRedirectKeywordAnalysis(),
        ),
        params: {
            ...filterConsistencyParams,
        },
        configId: "DemandSearchTrends",
        configIdDeps: ["KeywordsGenerator", "KeywordsGeneratorV2"],
        pageId: {
            section: "keywordAnalysis",
            subSection: "demandSearchTrends",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Search Interest Analysis",
            subSubSection: "DemandSearchTrends",
        },
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: BasicDurations.LAST_TWELVE_MONTHS,
            country: WORLDWIDE_COUNTRY_ID,
        },
        data: {
            pageViewTracking: demandAnalysisSearchTrendsPageViewTracking,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        pageTitle: "demand.analysis.home.title",
        pageSubtitle: "KeywordAnalysis.demand.search.trends.title.tooltip",
        icon: "sw-icon-nav-total",
        homeState: "digitalsuite_marketresearch_keywordmarketanalysis_home",
        periodOverPeriodEnabled: true,
        showCompareButton: true,
    },
    digitalsuite_marketresearch_keywordmarketanalysis_sneakpeekQuery: {
        parent: "digitalsuite_marketresearch_keywordmarketanalysis_search",
        url: "/sneakpeek/query?editedId",
        component: SneakpeekQuery,
        configId: "KeywordSneakpeek",
        pageId: {
            section: "keywordAnalysis",
            subSection: "sneakpeek",
            subSubSection: "query",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "sneakpeek",
            subSubSection: "query",
        },
        icon: "sw-icon-overview",
        pageTitle: "Create Data Prototype",
        hidePageTitle: true,
        isUSStatesSupported: true,
    },
    digitalsuite_marketresearch_keywordmarketanalysis_sneakpeekResults: {
        parent: "digitalsuite_marketresearch_keywordmarketanalysis_search",
        url: "/sneakpeek/results?queryId",
        component: SneakpeekResultsPage,
        configId: "KeywordSneakpeek",
        pageId: {
            section: "keywordAnalysis",
            subSection: "sneakpeek",
            subSubSection: "results",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "sneakpeek",
            subSubSection: "results",
        },
        icon: "sw-icon-overview",
        pageTitle: "Data Prototype Results",
        hidePageTitle: true,
        isUSStatesSupported: true,
    },
};
